import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Home, Building2, Building, Layers, Trees, Map, Warehouse, Plus, X,
} from 'lucide-react';

/**
 * Shared, presentational preference fields used by the agent-facing buying
 * preference editors (Find Buyers modal, Who-to-Call modal). They mirror the
 * buyer-portal Buying Preferences page so what agents capture lines up 1:1 with
 * what buyers pick themselves — tick-box property types and a click-to-confirm
 * suburb picker, so agents never free-type a mis-spelled suburb.
 */

// Portal's 8 property types, same ids the matcher and BuyingPreference store.
export const PROPERTY_TYPES_META = [
    { id: 'Apartment', label: 'Apartment', icon: Building2 },
    { id: 'Unit', label: 'Unit', icon: Building2 },
    { id: 'House', label: 'House', icon: Home },
    { id: 'Townhouse', label: 'Townhouse', icon: Building },
    { id: 'Villa', label: 'Villa', icon: Warehouse },
    { id: 'House and Land', label: 'House and Land', icon: Map },
    { id: 'Land', label: 'Land', icon: Trees },
    { id: 'Block of Units', label: 'Block of Units', icon: Layers },
];

export const POPULAR_CITIES = ['Melbourne', 'Sydney', 'Brisbane', 'Gold Coast', 'Canberra', 'Townsville', 'Perth', 'Adelaide'];

export const POPULAR_SUBURBS = [
    'Northcote', 'Brunswick', 'Fitzroy', 'Richmond', 'South Yarra', 'Preston',
    'Camberwell', 'Brighton', 'Footscray', 'Parramatta', 'Bondi', 'Chatswood',
    'Fortitude Valley', 'New Farm', 'Subiaco', 'Glenelg',
];

// Local reference list so the click-to-confirm dropdown works with no external
// API key. Mirrors the portal's COMMON_REGIONS.
export const SUBURB_REGIONS = [
    { name: 'Victoria', type: 'state' }, { name: 'New South Wales', type: 'state' },
    { name: 'Queensland', type: 'state' }, { name: 'Western Australia', type: 'state' },
    { name: 'South Australia', type: 'state' }, { name: 'Tasmania', type: 'state' },
    { name: 'Northern Territory', type: 'state' }, { name: 'Australian Capital Territory', type: 'state' },
    // Melbourne inner
    'Northcote', 'Brunswick', 'Fitzroy', 'Collingwood', 'Richmond', 'South Yarra', 'St Kilda',
    'Prahran', 'Camberwell', 'Kew', 'Hawthorn', 'Malvern', 'Carlton', 'Parkville', 'Abbotsford',
    'Clifton Hill', 'Alphington', 'Fairfield', 'Ivanhoe', 'Ivanhoe East', 'Heidelberg', 'Preston', 'Reservoir',
    'Coburg', 'Thornbury',
    // Banyule / Heidelberg area
    'Bellfield', 'Heidelberg Heights', 'Heidelberg West', 'West Heidelberg', 'Eaglemont',
    'Rosanna', 'Viewbank', 'Yallambie', 'Macleod', 'Watsonia', 'Watsonia North',
    'Lower Plenty', 'Montmorency', 'Briar Hill', 'St Helena',
    // Melbourne north
    'Doreen', 'Mernda', 'South Morang', 'Mill Park', 'Epping', 'Bundoora', 'Greensborough',
    'Eltham', 'Diamond Creek', 'Hurstbridge', 'Whittlesea', 'Craigieburn', 'Broadmeadows',
    // Melbourne east
    'Glen Waverley', 'Box Hill', 'Doncaster', 'Templestowe', 'Balwyn', 'Blackburn', 'Ringwood',
    'Croydon', 'Lilydale', 'Mooroolbark', 'Mount Waverley', 'Burwood', 'Vermont',
    // Melbourne south-east
    'Clayton', 'Oakleigh', 'Chadstone', 'Mulgrave', 'Springvale', 'Dandenong', 'Noble Park',
    'Keysborough', 'Berwick', 'Cranbourne', 'Narre Warren', 'Pakenham', 'Clyde', 'Clyde North',
    // Melbourne west
    'Footscray', 'Yarraville', 'Williamstown', 'Altona', 'Werribee', 'Point Cook', 'Tarneit',
    'Hoppers Crossing', 'Sunshine', 'St Albans', 'Caroline Springs', 'Melton',
    // Bayside
    'Brighton', 'Sandringham', 'Mentone', 'Mordialloc', 'Chelsea', 'Frankston', 'Mornington',
    'Caulfield', 'Caulfield South', 'Caulfield North', 'Hadfield', 'Pascoe Vale', 'Oak Park', 'Fawkner',
    // Sydney
    'Parramatta', 'Chatswood', 'Bondi', 'Manly', 'Newtown', 'Surry Hills', 'Paddington', 'Randwick',
    'Cronulla', 'Liverpool', 'Blacktown', 'Penrith',
    // Brisbane
    'Fortitude Valley', 'South Brisbane', 'West End', 'New Farm', 'Toowong', 'Indooroopilly',
    'Chermside', 'Carindale',
    // Perth
    'Fremantle', 'Subiaco', 'Cottesloe', 'Scarborough', 'Joondalup',
    // Adelaide
    'Glenelg', 'North Adelaide', 'Norwood', 'Unley',
    // Regional VIC
    'Geelong', 'Ballarat', 'Bendigo', 'Shepparton', 'Warrnambool', 'Mildura', 'Wangaratta', 'Wodonga',
    // Cities
    ...['Melbourne', 'Sydney', 'Brisbane', 'Gold Coast', 'Canberra', 'Townsville', 'Perth', 'Adelaide']
        .map((n) => ({ name: n, type: 'city' })),
].map((r) => (typeof r === 'string' ? { name: r, type: 'locality' } : r));

export const toTitleCase = (str) =>
    (str || '').toLowerCase().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** Multi-select tick tiles for property type. `value` is an array of type ids. */
export function PropertyTypeTiles({ value = [], onChange, accent = 'teal' }) {
    const on = accent === 'teal'
        ? 'bg-teal-50 border-teal-400 text-teal-700'
        : 'bg-indigo-50 border-indigo-400 text-indigo-700';
    const iconOn = accent === 'teal' ? 'bg-teal-100 text-teal-700' : 'bg-indigo-100 text-indigo-700';
    const toggle = (id) =>
        onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PROPERTY_TYPES_META.map(({ id, label, icon: Icon }) => {
                const sel = value.includes(id);
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => toggle(id)}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-all ${
                            sel ? on : 'bg-white border-gray-300 text-gray-600 hover:border-teal-300 hover:bg-gray-50'
                        }`}
                    >
                        <span className={`p-1 rounded-full ${sel ? iconOn : 'bg-gray-100 text-gray-500'}`}>
                            <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-[12px] font-semibold">{label}</span>
                    </button>
                );
            })}
        </div>
    );
}

/** Click-to-confirm suburb picker. `value` is an array of suburb strings. */
export function SuburbPicker({ value = [], onChange }) {
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    const build = useCallback((q) => {
        const query = q.trim().toLowerCase();
        if (query.length < 2) return [];
        const seen = new Set();
        const matches = [];
        for (const r of SUBURB_REGIONS) {
            if (r.name.toLowerCase().includes(query) && !seen.has(r.name.toLowerCase())) {
                seen.add(r.name.toLowerCase());
                matches.push(r);
            }
        }
        const rank = { locality: 1, city: 2, state: 3 };
        matches.sort((a, b) => {
            const ax = a.name.toLowerCase() === query ? 0 : 1;
            const bx = b.name.toLowerCase() === query ? 0 : 1;
            if (ax !== bx) return ax - bx; // exact-name match first
            return (rank[a.type] || 4) - (rank[b.type] || 4);
        });
        // Only offer the free-type "Add X" when it isn't already a known suburb.
        const out = [];
        if (!seen.has(query)) out.push({ name: toTitleCase(query), type: 'manual', isManual: true });
        out.push(...matches);
        return out.slice(0, 8);
    }, []);

    const onType = (v) => {
        setInput(v);
        const s = build(v);
        setSuggestions(s);
        setOpen(s.length > 0);
    };

    const add = (name) => {
        const clean = toTitleCase((name || '').trim());
        if (clean && !value.some((s) => s.toLowerCase() === clean.toLowerCase())) {
            onChange([...value, clean]);
        }
        setInput('');
        setSuggestions([]);
        setOpen(false);
    };

    const remove = (name) => onChange(value.filter((s) => s !== name));

    useEffect(() => {
        const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', away);
        return () => document.removeEventListener('mousedown', away);
    }, []);

    return (
        <div className="space-y-2.5">
            <div className="relative" ref={boxRef}>
                <div className="flex items-stretch gap-2">
                    <input
                        value={input}
                        onChange={(e) => onType(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
                        onFocus={() => suggestions.length > 0 && setOpen(true)}
                        placeholder="Search for a suburb…"
                        autoComplete="off"
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                    />
                    <button type="button" onClick={() => add(input)}
                        className="shrink-0 inline-flex items-center gap-1 px-3 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold">
                        <Plus className="w-4 h-4" /> Add
                    </button>
                </div>
                {open && suggestions.length > 0 && (
                    <div className="absolute z-30 w-full mt-1.5 bg-white rounded-lg border border-gray-200 shadow-xl max-h-60 overflow-y-auto">
                        {suggestions.map((s, i) => (
                            <button key={i} type="button" onClick={() => add(s.name)}
                                className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b border-gray-100 last:border-0 flex items-center gap-2.5">
                                <span className="p-1 bg-gray-100 rounded">
                                    {s.isManual ? <Plus className="w-3 h-3 text-teal-500" /> : <Map className="w-3 h-3 text-gray-500" />}
                                </span>
                                <span className="text-[13px] text-gray-800">
                                    {s.isManual ? `Add "${s.name}"` : s.name}
                                </span>
                                {!s.isManual && (
                                    <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">
                                        {s.type}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Popular quick-add */}
            <div className="flex flex-wrap gap-1.5">
                {POPULAR_SUBURBS.map((s) => {
                    const picked = value.some((v) => v.toLowerCase() === s.toLowerCase());
                    return (
                        <button key={s} type="button" onClick={() => add(s)} disabled={picked}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all ${
                                picked ? 'bg-teal-50 text-teal-700 border-teal-300 cursor-default'
                                    : 'bg-white border-gray-300 text-gray-600 hover:border-teal-300 hover:bg-teal-50'
                            }`}>
                            {s}
                        </button>
                    );
                })}
            </div>

            {/* Selected chips */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {value.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-100 text-teal-800 text-[12px] font-semibold rounded-md border border-teal-300">
                            {s}
                            <button type="button" onClick={() => remove(s)} className="hover:scale-110">
                                <X className="w-3 h-3" strokeWidth={3} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
